import { useEffect, useRef, useState } from 'react';
import { Card } from '../atoms/Card';
import { Heading } from '../atoms/Typography';
import { Text } from '../atoms/Typography';
import { Inline } from '../atoms/Inline';
import { Stack } from '../atoms/Stack';
import { Button } from '../atoms/Button';
import { Textarea } from '../atoms/Textarea/Textarea';
import { Badge } from '../atoms/Badge';
import { Icon } from '../atoms/Icon/Icon';
import { useTranslation } from '../../i18n/useTranslation';
import { useAiFeedback, AiFeedbackOutputType } from './useAiFeedback';
import './AiFeedbackControl.css';
import { Alert } from '../atoms/Alert';
import { LiveRegion } from '../atoms/LiveRegion';

type ThumbValue = 'up' | 'down';

const MAX_COMMENT_LENGTH = 1000;

/**
 * Screen-specific copy for the control. When given, the control renders the prompt
 * as text instead of its own card heading (the surrounding Section title names it),
 * and the thumbs buttons carry visible text labels next to their icons.
 */
export interface AiFeedbackControlLabels {
  prompt: string;
  up: string;
  down: string;
  commentLabel: string;
  submit: string;
}

export interface AiFeedbackControlProps {
  outputType: AiFeedbackOutputType;
  outputId: string;
  aquariumId: string;
  labels?: AiFeedbackControlLabels;
}

export function AiFeedbackControl({ outputType, outputId, aquariumId, labels }: AiFeedbackControlProps) {
  const { t } = useTranslation();
  const {
    rating,
    comment: savedComment,
    isLoading,
    isRatingSubmitting,
    isCommentSubmitting,
    error,
    hasRating,
    setRating,
    submitComment,
    clearError,
    retry,
  } = useAiFeedback({ outputType, outputId, aquariumId });

  const isMutating = isRatingSubmitting || isCommentSubmitting;
  const isError = error !== null;

  const [commentDraft, setCommentDraft] = useState<string>(savedComment ?? '');
  const [announcement, setAnnouncement] = useState<string>('');
  const [localError, setLocalError] = useState<boolean>(false);

  const retryButtonRef = useRef<HTMLButtonElement>(null);
  const thumbsUpRef = useRef<HTMLButtonElement>(null);
  const thumbsDownRef = useRef<HTMLButtonElement>(null);

  // Keep local draft in sync with hydrated/saved comment whenever it changes externally
  useEffect(() => {
    setCommentDraft(savedComment ?? '');
  }, [savedComment]);

  useEffect(() => {
    if (isError) {
      setLocalError(true);
    }
  }, [isError]);

  // Map the rating stored by the hook to the thumb value used by this UI
  const ratingValue: ThumbValue | null =
    rating === 'up' ? 'up' : rating === 'down' ? 'down' : null;

  useEffect(() => {
    if (localError && retryButtonRef.current) {
      retryButtonRef.current.focus();
    }
  }, [localError]);

  const handleThumbClick = (value: ThumbValue) => {
    setLocalError(false);
    clearError();
    const nextRating = ratingValue === value ? null : value;
    const activeRef = value === 'up' ? thumbsUpRef : thumbsDownRef;
    if (nextRating === null) {
      setRating(value);
      activeRef.current?.focus();
      return;
    }
    setRating(nextRating);
    setAnnouncement(t('aiFeedback.status.saved'));
    activeRef.current?.focus();
  };

  useEffect(() => {
    if (!isRatingSubmitting && !isError && ratingValue !== null) {
      setAnnouncement(t('aiFeedback.status.saved'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRatingSubmitting]);

  const handleRetry = async () => {
    setLocalError(false);
    clearError();
    try {
      await retry();
      setAnnouncement(t('aiFeedback.status.saved'));
    } catch {
      setLocalError(true);
      setAnnouncement(t('aiFeedback.error.submitFailed'));
    }
  };

  const handleCommentSave = () => {
    setLocalError(false);
    clearError();
    submitComment(commentDraft);
  };

  useEffect(() => {
    if (!isCommentSubmitting && isError) {
      setLocalError(true);
      setAnnouncement(t('aiFeedback.error.submitFailed'));
    } else if (!isCommentSubmitting && !isError) {
      setAnnouncement(t('aiFeedback.status.saved'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCommentSubmitting]);

  const commentUnchanged = commentDraft === (savedComment ?? '');
  const commentTooLong = commentDraft.length > MAX_COMMENT_LENGTH;
  const canSubmitComment = hasRating && !commentUnchanged && !commentTooLong && !isMutating;

  const showError = localError;
  const disableThumbs = isLoading || isMutating;

  return (
    <Card className="ai-feedback-control" data-testid="ai-feedback-control">
      <Stack gap="2">
        {labels ? (
          <Text>{labels.prompt}</Text>
        ) : (
          <>
            <Heading level={3}>{t('aiFeedback.card.title')}</Heading>
            <Text>{t('aiFeedback.card.body')}</Text>
          </>
        )}

        <Inline gap="2" align="center" className="ai-feedback-control__thumbs">
          <Button
            ref={thumbsUpRef}
            variant={ratingValue === 'up' ? 'primary' : 'ghost'}
            size="sm"
            disabled={disableThumbs}
            aria-pressed={ratingValue === 'up'}
            aria-label={labels ? undefined : t('aiFeedback.thumbsUp.label')}
            onClick={() => handleThumbClick('up')}
          >
            <Icon name="thumbs-up" />
            {labels ? <span className="ai-feedback-control__thumb-label">{labels.up}</span> : null}
          </Button>
          <Button
            ref={thumbsDownRef}
            variant={ratingValue === 'down' ? 'primary' : 'ghost'}
            size="sm"
            disabled={disableThumbs}
            aria-pressed={ratingValue === 'down'}
            aria-label={labels ? undefined : t('aiFeedback.thumbsDown.label')}
            onClick={() => handleThumbClick('down')}
          >
            <Icon name="thumbs-down" />
            {labels ? <span className="ai-feedback-control__thumb-label">{labels.down}</span> : null}
          </Button>
          {isLoading && <Text>{t('aiFeedback.status.loading')}</Text>}
          {!isLoading && !showError && hasRating && (
            <Badge tone="success">{t('aiFeedback.status.saved')}</Badge>
          )}
        </Inline>

        {!isLoading && !showError && hasRating && (
          <Stack gap="1">
            <Textarea
              label={labels?.commentLabel ?? t('aiFeedback.comment.label')}
              placeholder={t('aiFeedback.comment.placeholder')}
              maxLength={MAX_COMMENT_LENGTH}
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
            />
            <Inline gap="2" align="center">
              <Button
                variant="secondary"
                size="sm"
                disabled={!canSubmitComment}
                onClick={handleCommentSave}
              >
                {labels?.submit ?? t('aiFeedback.comment.save')}
              </Button>
              <Text>
                {t('aiFeedback.comment.counter', { count: commentDraft.length })}
              </Text>
            </Inline>
          </Stack>
        )}

        {!isLoading && !showError && !hasRating && (
          <Text>{t('aiFeedback.comment.hint')}</Text>
        )}

        {showError && (
          // Announced once through the live region below (focus also moves to the retry
          // button, which describes itself with the message) — so the Alert itself is silent.
          <Alert
            live="off"
            compact
            action={
              <Button
                ref={retryButtonRef}
                variant="secondary"
                size="sm"
                aria-describedby="ai-feedback-error"
                onClick={handleRetry}
              >
                {t('aiFeedback.error.retry')}
              </Button>
            }
          >
            <span id="ai-feedback-error">{t('aiFeedback.error.submitFailed')}</span>
          </Alert>
        )}

        <LiveRegion visuallyHidden>{announcement}</LiveRegion>
      </Stack>
    </Card>
  );
}
